import { toast } from 'sonner';
import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { LevelTypes } from '@/types';
import { useGetAddToDatabaseById } from '@/hooks';
import { addToDatabaseTreeItemService } from '@/services/form/add-to-database-tree-item.service';
import { addToDatabaseService } from '@/services/form/add-to-database.service';
import { useQueryClient } from '@tanstack/react-query';

export default function AddToDatabaseDesign() {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data, isLoading } = useGetAddToDatabaseById(id || '');
    const [levels, setLevels] = useState<LevelTypes[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const dbLevels: LevelTypes[] = data?.data?.levels ?? [];
        if (dbLevels.length > 0) {
            setLevels(dbLevels);
        }
    }, [data]);

    if (isLoading) {
        return <p className='text-center py-5'>{t('processManagement.fetchingFromDatabase')}</p>;
    }

    const handleLevelNameChange = (index: number, value: string) => {
        const updated = [...levels];
        updated[index] = { ...updated[index], levelName: value };
        setLevels(updated);
    };

    const handleItemNameChange = (levelIndex: number, itemIndex: number, value: string) => {
        const updated = [...levels];
        updated[levelIndex].items[itemIndex] = { ...updated[levelIndex].items[itemIndex], itemName: value };
        setLevels(updated);
    };

    const handleAddLevel = () => {
        setLevels(prev => [
            ...prev,
            { levelId: `level-${Date.now()}`, levelName: '', items: [] },
        ]);
    };

    const handleAddItem = (levelIndex: number) => {
        const updated = [...levels];
        updated[levelIndex].items.push({ itemId: `item-${Date.now()}`, itemName: '', parentItemId: '' });
        setLevels(updated);
    };

    const handleRemoveLevel = (index: number) => {
        const removedLevelId = levels[index].levelId;
        const updated = levels.filter((_, i) => i !== index);
        updated.forEach(level => {
            level.items.forEach(item => {
                if (item.parentItemId === removedLevelId) item.parentItemId = '';
            });
        });
        setLevels(updated);
    };

    const handleRemoveItem = (levelIndex: number, itemIndex: number) => {
        const removedItemId = levels[levelIndex].items[itemIndex].itemId;
        const updated = [...levels];
        updated[levelIndex].items.splice(itemIndex, 1);
        updated.forEach(level => {
            level.items.forEach(item => {
                if (item.parentItemId === removedItemId) item.parentItemId = '';
            });
        });
        setLevels(updated);
    };

    const handleItemParentChange = (levelIndex: number, itemIndex: number, value: string) => {
        const updated = [...levels];
        updated[levelIndex].items[itemIndex] = { ...updated[levelIndex].items[itemIndex], parentItemId: value };
        setLevels(updated);
    };

    const handleSave = async () => {
        if (!id) return;
        setIsSaving(true);
        try {
            // Clear all existing tree items for this database
            const deleteResult = await addToDatabaseTreeItemService.deleteAllByAddToDatabaseId(id);
            if (!deleteResult?.success) {
                // Fallback: fetch and delete individually (handles cases where bulk-delete is unavailable)
                const existing = await addToDatabaseTreeItemService.getAllTreeItems();
                const items: any[] = (existing?.data ?? []).filter((item: any) => item.addToDatabaseId === id);
                for (const item of items) {
                    await addToDatabaseTreeItemService.deleteTreeItem(item.id);
                }
            }

            // Re-create items level by level, tracking old itemId → new server ID
            const idMap: Record<string, string> = {};

            for (let depth = 0; depth < levels.length; depth++) {
                const level = levels[depth];
                for (const item of level.items) {
                    if (!item.itemName.trim()) continue;
                    const parentServerId = depth === 0 ? null : (idMap[item.parentItemId] || null);
                    const result = await addToDatabaseTreeItemService.createTreeItem({
                        name: item.itemName,
                        status: 'ENABLED',
                        parentId: parentServerId,
                        addToDatabaseId: id,
                    });
                    if (result?.data?.id) {
                        idMap[item.itemId] = result.data.id;
                    }
                }
            }

            // Persist level names so they survive the tree-item transform
            const levelNamesPayload = levels.map((level, depth) => ({
                levelId: `level-${depth}`,
                levelName: level.levelName,
            }));
            await addToDatabaseService.updateAddToDatabase(id, { levelNames: levelNamesPayload });

            // Invalidate queries so the form dropdowns and tree view refresh
            queryClient.invalidateQueries({ queryKey: ['add-to-database', id] });
            queryClient.invalidateQueries({ queryKey: ['add-to-database-tree-items'] });

            toast.success(t('common.databaseItemUpdatedSuccessfully'));
        } catch (error) {
            console.error(error);
            toast.error(t('processManagement.failedToUpdate'));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-4 max-w-screen-md mx-auto p-6">
            <p className='text-lg font-medium text-black text-center py-3'>{data?.data?.name}</p>
            {levels.map((level, levelIndex) => (
                <div key={level.levelId} className="p-3 bg-white border-l-[2.4px] border-white focus-within:border-darkBlue rounded-md space-y-3">
                    <div className="flex space-x-4 items-center">
                        <label className="main-label">{t('processManagement.levelName')}</label>
                        <input
                            type="text"
                            value={level.levelName}
                            onChange={(e) => handleLevelNameChange(levelIndex, e.target.value)}
                            className="main-input"
                            placeholder={t('processManagement.levelNameInput')}
                        />
                        <button
                            type="button"
                            onClick={() => handleRemoveLevel(levelIndex)}
                            className="text-red-500 text-2xl"
                        >
                            <Icon icon="ic:outline-delete" />
                        </button>
                    </div>
                    {level.items.map((item, itemIndex) => (
                        <div key={item.itemId} className="flex space-x-4 items-center">
                            <input
                                type="text"
                                value={item.itemName}
                                onChange={(e) => handleItemNameChange(levelIndex, itemIndex, e.target.value)}
                                className="main-input"
                                placeholder={t('processManagement.itemName')}
                            />
                            {levelIndex > 0 && (
                                <select
                                    value={item.parentItemId}
                                    onChange={(e) => handleItemParentChange(levelIndex, itemIndex, e.target.value)}
                                    className="main-input"
                                >
                                    <option value="">{t('processManagement.selectParentItem')}</option>
                                    {levels[levelIndex - 1].items.map((parentItem) => (
                                        <option key={parentItem.itemId} value={parentItem.itemId}>
                                            {parentItem.itemName}
                                        </option>
                                    ))}
                                </select>
                            )}
                            <button
                                type="button"
                                onClick={() => handleRemoveItem(levelIndex, itemIndex)}
                                className="text-red-500"
                            >
                                X
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => handleAddItem(levelIndex)}
                        className="flex justify-between border-none hover:bg-inherit text-darkBlue items-center space-x-1 p-0"
                    >
                        <Icon icon="gg:add" fontSize={21} />
                        <p>{t('processManagement.addItem')}</p>
                    </button>
                </div>
            ))}
            <div className='flex justify-between items-center'>
                <button
                    type="button"
                    onClick={handleAddLevel}
                    className="main-dark-button"
                >
                    {t('processManagement.addLevel')}
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="main-dark-button"
                >
                    {isSaving ? t('processManagement.updating') : t('processManagement.updateAddToDatabase')}
                </button>
            </div>
        </div>
    );
}
